import { useState } from "react";
import { Check, Trash2, Plus, Upload, X, MapPin, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { NumberInput } from "../../../components/ui/number-input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { Checkbox } from "../../../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../../../components/ui/select";
import { toast } from "sonner";
import { 
  COUNTRIES, COUNTRY_STATES, COUNTRY_CITIES, INDIA_STATE_CITIES, TELECOM_OPERATORS,
  DEVICE_TYPES, DEVICE_MAKES, DEVICE_MODELS, OS_LIST, OS_VERSIONS, BROWSERS, CONNECTION_SPEEDS,
  AD_PLACEMENTS_DISPLAY, AD_PLACEMENTS_INCONTENT, AD_PLACEMENTS_NATIVE,
  SUPPLY_SOURCES
} from "../constants";

// Helper component for bulk input
function BulkInputSection({ title, items, onAdd, onRemove, placeholder, type = "include" }) {
  const [bulkInput, setBulkInput] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  
  const handleBulkAdd = () => {
    const newItems = bulkInput
      .split(/[\n,]+/)
      .map(item => item.trim())
      .filter(item => item && !items.includes(item));
    if (newItems.length > 0) {
      onAdd([...items, ...newItems]);
      setBulkInput("");
      setShowBulk(false);
      toast.success(`Added ${newItems.length} items`);
    }
  };

  const colorClass = type === "include" ? "10B981" : "EF4444";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-slate-600">{title}</Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowBulk(!showBulk)}
          className={`text-[#${colorClass}] hover:text-[#${colorClass}]`}
        >
          <Upload className="w-3 h-3 mr-1" />
          Bulk
        </Button>
      </div>
      
      {showBulk ? (
        <div className="space-y-2">
          <textarea
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
            placeholder={`Paste ${placeholder} (comma or newline separated)`}
            className="w-full h-24 p-2 rounded surface-primary border border-slate-200 text-slate-900 text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleBulkAdd} className={`bg-[#${colorClass}]`}>
              Add All
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowBulk(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Input
          placeholder={`Enter ${placeholder} and press Enter`}
          className="surface-primary border-slate-200 text-slate-900"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.value.trim()) {
              const val = e.target.value.trim();
              if (!items.includes(val)) {
                onAdd([...items, val]);
              }
              e.target.value = '';
            }
          }}
        />
      )}
      
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {items.map((item) => (
            <Badge key={item} variant="secondary" className={`bg-[#${colorClass}]/20 text-[#${colorClass}]`}>
              {item}
              <button onClick={() => onRemove(items.filter(i => i !== item))} className="ml-2">x</button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// Country/State/City selector component
function GeoSelector({ form, updateField, type = "include" }) {
  const suffix = type === "include" ? "" : "_exclude";
  const countriesField = `geo_countries${suffix}`;
  const statesField = `geo_states${suffix}`;
  const citiesField = `geo_cities${suffix}`;
  const pincodesField = `geo_pincodes${suffix}`;
  
  const countries = form[countriesField] || [];
  const states = form[statesField] || [];
  const cities = form[citiesField] || [];
  const pincodes = form[pincodesField] || [];
  
  const availableStates = countries.length === 1 ? (COUNTRY_STATES[countries[0]] || []) : [];
  
  // For India, get cities based on selected states with tier classification
  const getAvailableCities = () => {
    if (countries.length === 1 && countries[0] === "IND" && states.length > 0) {
      // Get cities for selected Indian states
      const allCities = [];
      states.forEach(state => {
        const stateCities = INDIA_STATE_CITIES[state];
        if (stateCities) {
          if (stateCities.tier1?.length > 0) {
            allCities.push({ tier: "Tier 1", cities: stateCities.tier1, state });
          }
          if (stateCities.tier2?.length > 0) {
            allCities.push({ tier: "Tier 2", cities: stateCities.tier2, state });
          }
          if (stateCities.tier3?.length > 0) {
            allCities.push({ tier: "Tier 3", cities: stateCities.tier3, state });
          }
        }
      });
      return allCities;
    }
    // For other countries, return flat list
    if (countries.length === 1) {
      const countryCities = COUNTRY_CITIES[countries[0]] || [];
      return countryCities.length > 0 ? [{ tier: "All Cities", cities: countryCities }] : [];
    }
    return [];
  };
  
  const availableCitiesByTier = getAvailableCities();
  const hasAvailableCities = availableCitiesByTier.length > 0;
  
  const colorClass = type === "include" ? "10B981" : "EF4444";
  
  return (
    <div className="space-y-4">
      {/* Countries */}
      <div className="space-y-2">
        <Label className="text-slate-600">Countries</Label>
        <Select 
          onValueChange={(v) => {
            if (v && !countries.includes(v)) {
              updateField(countriesField, [...countries, v]);
              if (type === "include") {
                updateField(statesField, []);
                updateField(citiesField, []);
              }
            }
          }}
        >
          <SelectTrigger className="surface-primary border-slate-200 text-slate-900">
            <SelectValue placeholder={`Select country to ${type}`} />
          </SelectTrigger>
          <SelectContent className="surface-primary border-slate-200 max-h-[300px]">
            {COUNTRIES.map((country) => (
              <SelectItem key={country.code} value={country.code} className="text-slate-900">
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {countries.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {countries.map((code) => (
              <Badge key={code} variant="secondary" className={`bg-[#${colorClass}]/20 text-[#${colorClass}]`}>
                {COUNTRIES.find(c => c.code === code)?.name || code}
                <button onClick={() => updateField(countriesField, countries.filter(c => c !== code))} className="ml-2">x</button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* States */}
      {availableStates.length > 0 && (
        <div className="space-y-2">
          <Label className="text-slate-600">States / Regions</Label>
          <Select onValueChange={(v) => {
            if (v && !states.includes(v)) {
              updateField(statesField, [...states, v]);
              // Clear cities when state changes for India
              if (countries[0] === "IND") {
                updateField(citiesField, []);
              }
            }
          }}>
            <SelectTrigger className="surface-primary border-slate-200 text-slate-900">
              <SelectValue placeholder="Select state/region" />
            </SelectTrigger>
            <SelectContent className="surface-primary border-slate-200 max-h-[300px]">
              {availableStates.map((state) => (
                <SelectItem key={state} value={state} className="text-slate-900">{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {states.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {states.map((state) => (
                <Badge key={state} variant="secondary" className={`bg-[#${colorClass}]/20 text-[#${colorClass}]`}>
                  {state}
                  <button onClick={() => updateField(statesField, states.filter(s => s !== state))} className="ml-2">x</button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cities - With Tier Classification for India */}
      {hasAvailableCities && (
        <div className="space-y-2">
          <Label className="text-slate-600">
            Cities {countries[0] === "IND" && states.length > 0 && `(${states.join(", ")})`}
          </Label>
          
          {/* Tier-wise city selection for India */}
          {countries[0] === "IND" && states.length > 0 ? (
            <div className="space-y-3">
              {availableCitiesByTier.map((tierGroup) => (
                <div key={`${tierGroup.tier}-${tierGroup.state || 'all'}`} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      tierGroup.tier === "Tier 1" ? "bg-[#10B981]/20 text-[#10B981]" :
                      tierGroup.tier === "Tier 2" ? "bg-[#3B82F6]/20 text-[#3B82F6]" :
                      "bg-[#F59E0B]/20 text-[#F59E0B]"
                    }`}>
                      {tierGroup.tier}
                    </span>
                    {tierGroup.state && <span className="text-xs text-slate-500">({tierGroup.state})</span>}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-xs h-6 px-2"
                      onClick={() => {
                        const newCities = [...cities, ...tierGroup.cities.filter(c => !cities.includes(c))];
                        updateField(citiesField, newCities);
                      }}
                    >
                      Select All
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tierGroup.cities.map((city) => {
                      const isSelected = cities.includes(city);
                      return (
                        <Badge 
                          key={city} 
                          variant="secondary" 
                          className={`cursor-pointer ${isSelected ? `bg-[#${colorClass}]/20 text-[#${colorClass}]` : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                          onClick={() => {
                            if (isSelected) {
                              updateField(citiesField, cities.filter(c => c !== city));
                            } else {
                              updateField(citiesField, [...cities, city]);
                            }
                          }}
                        >
                          {isSelected && <Check className="w-3 h-3 mr-1" />}
                          {city}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Standard city dropdown for other countries */
            <Select onValueChange={(v) => {
              if (v && !cities.includes(v)) {
                updateField(citiesField, [...cities, v]);
              }
            }}>
              <SelectTrigger className="surface-primary border-slate-200 text-slate-900">
                <SelectValue placeholder="Select city" />
              </SelectTrigger>
              <SelectContent className="surface-primary border-slate-200 max-h-[300px]">
                {availableCitiesByTier[0]?.cities.map((city) => (
                  <SelectItem key={city} value={city} className="text-slate-900">{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {cities.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-xs text-slate-500">Selected ({cities.length}):</span>
              {cities.map((city) => (
                <Badge key={city} variant="secondary" className={`bg-[#${colorClass}]/20 text-[#${colorClass}]`}>
                  {city}
                  <button onClick={() => updateField(citiesField, cities.filter(c => c !== city))} className="ml-2">x</button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pincodes */}
      <BulkInputSection
        title="Pincodes / ZIP Codes"
        items={pincodes}
        onAdd={(items) => updateField(pincodesField, items)}
        onRemove={(items) => updateField(pincodesField, items)}
        placeholder="pincode"
        type={type}
      />
    </div>
  );
}

export function TargetingStep({ form, updateField }) {
  const availableOperators = form.geo_countries.length === 1 
    ? (TELECOM_OPERATORS[form.geo_countries[0]] || []) 
    : [];
  
  const availableOSVersions = form.os_list.flatMap(os => 
    (OS_VERSIONS[os] || []).map(v => `${v}`)
  );

  const addLatLongPoint = () => {
    if (form.geo_latitude && form.geo_longitude) {
      const newPoint = {
        id: Date.now().toString(),
        lat: parseFloat(form.geo_latitude),
        lon: parseFloat(form.geo_longitude),
        radius_km: form.radius_km,
        type: form.lat_long_type
      };
      updateField("lat_long_points", [...form.lat_long_points, newPoint]);
      updateField("geo_latitude", "");
      updateField("geo_longitude", "");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-1">Targeting</h2>
        <p className="text-sm text-slate-500">Define geographic, device, inventory, and technical targeting</p>
      </div>

      <Tabs defaultValue="geo" className="w-full">
        <TabsList className="grid w-full grid-cols-6 bg-slate-50">
          <TabsTrigger value="geo" className="data-[state=active]:bg-[#3B82F6] text-xs">Geography</TabsTrigger>
          <TabsTrigger value="device" className="data-[state=active]:bg-[#3B82F6] text-xs">Device</TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-[#3B82F6] text-xs">Inventory</TabsTrigger>
          <TabsTrigger value="supply" className="data-[state=active]:bg-[#3B82F6] text-xs">Supply</TabsTrigger>
          <TabsTrigger value="contextual" className="data-[state=active]:bg-[#3B82F6] text-xs">Contextual</TabsTrigger>
          <TabsTrigger value="technical" className="data-[state=active]:bg-[#3B82F6] text-xs">Technical</TabsTrigger>
        </TabsList>

        {/* Geography Tab */}
        <TabsContent value="geo" className="space-y-4 mt-4">
          <Card className="surface-secondary border-[#10B981]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#10B981] flex items-center gap-2">
                <Check className="w-4 h-4" /> Include Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <GeoSelector form={form} updateField={updateField} type="include" />
            </CardContent>
          </Card>

          <Card className="surface-secondary border-[#EF4444]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#EF4444] flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Exclude Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <GeoSelector form={form} updateField={updateField} type="exclude" />
            </CardContent>
          </Card>

          {/* Telecom Operators */}
          {availableOperators.length > 0 && (
            <Card className="surface-secondary border-[#8B5CF6]/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#8B5CF6]">Telecom Operators</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {availableOperators.map((op) => (
                    <Badge
                      key={op}
                      variant="secondary"
                      className={`cursor-pointer ${
                        form.telecom_operators.includes(op)
                          ? "bg-[#8B5CF6]/20 text-[#8B5CF6]"
                          : "bg-slate-100 text-slate-500 hover:bg-[#8B5CF6]/10"
                      }`}
                      onClick={() => {
                        const ops = form.telecom_operators.includes(op)
                          ? form.telecom_operators.filter(o => o !== op)
                          : [...form.telecom_operators, op];
                        updateField("telecom_operators", ops);
                      }}
                    >
                      {op}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lat/Long Targeting */}
          <Card className="surface-secondary border-[#F59E0B]/30">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-[#F59E0B] flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Lat/Long Radius Targeting
                </CardTitle>
                <Switch
                  checked={form.lat_long_targeting}
                  onCheckedChange={(v) => updateField("lat_long_targeting", v)}
                />
              </div>
            </CardHeader>
            {form.lat_long_targeting && (
              <CardContent className="space-y-4">
                {/* Manual Entry */}
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 font-medium">Add Single Point</p>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-slate-600 text-xs">Latitude</Label>
                      <NumberInput
                        step="0.0001"
                        value={form.geo_latitude}
                        onChange={(e) => updateField("geo_latitude", e.target.value)}
                        placeholder="e.g. 40.7128"
                        className="surface-primary border-slate-200 text-slate-900"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-600 text-xs">Longitude</Label>
                      <NumberInput
                        step="0.0001"
                        value={form.geo_longitude}
                        onChange={(e) => updateField("geo_longitude", e.target.value)}
                        placeholder="e.g. -74.0060"
                        className="surface-primary border-slate-200 text-slate-900"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-600 text-xs">Radius (km)</Label>
                      <NumberInput
                        value={form.radius_km}
                        onChange={(e) => updateField("radius_km", parseInt(e.target.value) || 10)}
                        className="surface-primary border-slate-200 text-slate-900"
                        min={1}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-600 text-xs">Type</Label>
                      <Select value={form.lat_long_type} onValueChange={(v) => updateField("lat_long_type", v)}>
                        <SelectTrigger className="surface-primary border-slate-200 text-slate-900">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="surface-primary border-slate-200">
                          <SelectItem value="include">Include</SelectItem>
                          <SelectItem value="exclude">Exclude</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={addLatLongPoint}
                    disabled={!form.geo_latitude || !form.geo_longitude}
                    className="bg-[#F59E0B] hover:bg-[#D97706]"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Point
                  </Button>
                </div>

                {/* Bulk Upload Section */}
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500 font-medium">Bulk Upload (CSV)</p>
                    <a 
                      href="data:text/csv;charset=utf-8,latitude,longitude,radius_km,type%0A40.7128,-74.0060,10,include%0A34.0522,-118.2437,15,include%0A51.5074,-0.1278,20,exclude"
                      download="lat_long_template.csv"
                      className="text-xs text-[#3B82F6] hover:underline flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" /> Download Template
                    </a>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            try {
                              const text = event.target?.result;
                              const lines = text.split('\n').filter(line => line.trim());
                              const newPoints = [];
                              
                              // Skip header row if present
                              const startIndex = lines[0].toLowerCase().includes('latitude') ? 1 : 0;
                              
                              for (let i = startIndex; i < lines.length; i++) {
                                const values = lines[i].split(',').map(v => v.trim());
                                if (values.length >= 2) {
                                  const lat = parseFloat(values[0]);
                                  const lon = parseFloat(values[1]);
                                  const radius = values[2] ? parseFloat(values[2]) : 10;
                                  const type = values[3]?.toLowerCase() === 'exclude' ? 'exclude' : 'include';
                                  
                                  if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
                                    newPoints.push({
                                      id: `${Date.now()}-${i}`,
                                      lat,
                                      lon,
                                      radius_km: isNaN(radius) ? 10 : radius,
                                      type
                                    });
                                  }
                                }
                              }
                              
                              if (newPoints.length > 0) {
                                updateField("lat_long_points", [...form.lat_long_points, ...newPoints]);
                                toast.success(`Added ${newPoints.length} location points`);
                              } else {
                                toast.error("No valid points found in CSV");
                              }
                            } catch (err) {
                              toast.error("Failed to parse CSV file");
                            }
                          };
                          reader.readAsText(file);
                          e.target.value = ''; // Reset input
                        }
                      }}
                      className="surface-primary border-slate-200 text-slate-900 text-sm"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400">
                    CSV format: latitude, longitude, radius_km (optional, default 10), type (optional: include/exclude, default include)
                  </p>
                </div>

                {/* Points List */}
                {form.lat_long_points.length > 0 && (
                  <div className="border-t border-slate-200 pt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500 font-medium">
                        Location Points ({form.lat_long_points.length})
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateField("lat_long_points", [])}
                        className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10 h-6 text-xs"
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {form.lat_long_points.map((point) => (
                        <div key={point.id} className={`flex items-center justify-between p-2 rounded text-sm ${
                          point.type === "include" ? "bg-[#10B981]/10" : "bg-[#EF4444]/10"
                        }`}>
                          <span className="text-slate-900">
                            {point.lat.toFixed(4)}, {point.lon.toFixed(4)} ({point.radius_km}km) - {point.type}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => updateField("lat_long_points", form.lat_long_points.filter(p => p.id !== point.id))}
                          >
                            <X className="w-3 h-3 text-[#EF4444]" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* Device Tab */}
        <TabsContent value="device" className="space-y-4 mt-4">
          <Card className="surface-secondary border-[#3B82F6]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#3B82F6]">Device Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-3">
                {DEVICE_TYPES.map((device) => {
                  const Icon = device.icon;
                  const isSelected = form.device_types.includes(device.value);
                  return (
                    <div
                      key={device.value}
                      onClick={() => {
                        const types = isSelected
                          ? form.device_types.filter(t => t !== device.value)
                          : [...form.device_types, device.value];
                        updateField("device_types", types);
                      }}
                      className={`p-4 rounded-lg cursor-pointer border text-center ${
                        isSelected
                          ? "bg-[#3B82F6]/20 border-[#3B82F6]"
                          : "surface-primary border-slate-200 hover:border-[#3B82F6]/50"
                      }`}
                    >
                      <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? "text-[#3B82F6]" : "text-slate-500"}`} />
                      <p className="text-sm text-slate-900">{device.label}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Device Make & Model */}
          <Card className="surface-secondary border-[#F59E0B]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#F59E0B]">Device Make & Model</CardTitle>
              <CardDescription className="text-xs text-slate-500">Target specific device manufacturers and models</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Device Makes */}
              <div className="space-y-2">
                <Label className="text-slate-600">Device Makes (Manufacturers)</Label>
                <Select 
                  onValueChange={(v) => {
                    if (v && !form.device_makes.includes(v)) {
                      updateField("device_makes", [...form.device_makes, v]);
                    }
                  }}
                >
                  <SelectTrigger className="surface-primary border-slate-200 text-slate-900">
                    <SelectValue placeholder="Select device manufacturer" />
                  </SelectTrigger>
                  <SelectContent className="surface-primary border-slate-200 max-h-[300px]">
                    {DEVICE_MAKES.map((make) => (
                      <SelectItem key={make.value} value={make.value} className="text-slate-900">
                        {make.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.device_makes.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.device_makes.map((make) => (
                      <Badge key={make} variant="secondary" className="bg-[#F59E0B]/20 text-[#F59E0B]">
                        {make}
                        <button 
                          onClick={() => {
                            updateField("device_makes", form.device_makes.filter(m => m !== make));
                            // Also remove models from this make
                            const makeModels = DEVICE_MODELS[make] || [];
                            updateField("device_models", form.device_models.filter(m => !makeModels.includes(m)));
                          }} 
                          className="ml-2"
                        >
                          x
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Device Models - shown when makes are selected */}
              {form.device_makes.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-slate-600">Device Models</Label>
                  {form.device_makes.map((make) => {
                    const models = DEVICE_MODELS[make] || [];
                    if (models.length === 0) return null;
                    
                    return (
                      <div key={make} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium px-2 py-1 rounded bg-[#F59E0B]/20 text-[#F59E0B]">
                            {make}
                          </span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-xs h-6 px-2"
                            onClick={() => {
                              const newModels = [...form.device_models, ...models.filter(m => !form.device_models.includes(m))];
                              updateField("device_models", newModels);
                            }}
                          >
                            Select All
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-xs h-6 px-2 text-[#EF4444]"
                            onClick={() => {
                              updateField("device_models", form.device_models.filter(m => !models.includes(m)));
                            }}
                          >
                            Clear
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {models.map((model) => {
                            const isSelected = form.device_models.includes(model);
                            return (
                              <Badge 
                                key={model} 
                                variant="secondary" 
                                className={`cursor-pointer ${isSelected ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                onClick={() => {
                                  if (isSelected) {
                                    updateField("device_models", form.device_models.filter(m => m !== model));
                                  } else {
                                    updateField("device_models", [...form.device_models, model]);
                                  }
                                }}
                              >
                                {isSelected && <Check className="w-3 h-3 mr-1" />}
                                {model}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  {form.device_models.length > 0 && (
                    <div className="pt-2 border-t border-slate-200">
                      <span className="text-xs text-slate-500">Selected Models ({form.device_models.length})</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="surface-secondary border-[#10B981]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#10B981]">Operating Systems</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {OS_LIST.map((os) => (
                  <Badge
                    key={os.value}
                    variant="secondary"
                    className={`cursor-pointer ${
                      form.os_list.includes(os.value)
                        ? "bg-[#10B981]/20 text-[#10B981]"
                        : "bg-slate-100 text-slate-500 hover:bg-[#10B981]/10"
                    }`}
                    onClick={() => {
                      const list = form.os_list.includes(os.value)
                        ? form.os_list.filter(o => o !== os.value)
                        : [...form.os_list, os.value];
                      updateField("os_list", list);
                    }}
                  >
                    {os.label}
                  </Badge>
                ))}
              </div>
              
              {/* OS Versions */}
              {availableOSVersions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-600">OS Versions</Label>
                  <div className="flex flex-wrap gap-2">
                    {availableOSVersions.map((version) => (
                      <Badge
                        key={version}
                        variant="secondary"
                        className={`cursor-pointer ${
                          form.os_versions.includes(version)
                            ? "bg-[#10B981]/20 text-[#10B981]"
                            : "bg-slate-100 text-slate-500 hover:bg-[#10B981]/10"
                        }`}
                        onClick={() => {
                          const versions = form.os_versions.includes(version)
                            ? form.os_versions.filter(v => v !== version)
                            : [...form.os_versions, version];
                          updateField("os_versions", versions);
                        }}
                      >
                        {version}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Connection Speed */}
          <Card className="surface-secondary border-[#8B5CF6]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#8B5CF6]">Connection Speed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {CONNECTION_SPEEDS.map((speed) => (
                  <Badge
                    key={speed.value}
                    variant="secondary"
                    className={`cursor-pointer ${
                      form.connection_types.includes(speed.value)
                        ? "bg-[#8B5CF6]/20 text-[#8B5CF6]"
                        : "bg-slate-100 text-slate-500 hover:bg-[#8B5CF6]/10"
                    }`}
                    onClick={() => {
                      const types = form.connection_types.includes(speed.value)
                        ? form.connection_types.filter(t => t !== speed.value)
                        : [...form.connection_types, speed.value];
                      updateField("connection_types", types);
                    }}
                  >
                    {speed.label}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4 mt-4">
          <Card className="surface-secondary border-[#10B981]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#10B981]">Domain/URL Allowlist</CardTitle>
              <CardDescription className="text-xs text-slate-500">Only bid on these domains</CardDescription>
            </CardHeader>
            <CardContent>
              <BulkInputSection
                title=""
                items={form.domain_allowlist}
                onAdd={(items) => updateField("domain_allowlist", items)}
                onRemove={(items) => updateField("domain_allowlist", items)}
                placeholder="domain (e.g. example.com)"
                type="include"
              />
            </CardContent>
          </Card>

          <Card className="surface-secondary border-[#EF4444]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#EF4444]">Domain/URL Blocklist</CardTitle>
              <CardDescription className="text-xs text-slate-500">Never bid on these domains</CardDescription>
            </CardHeader>
            <CardContent>
              <BulkInputSection
                title=""
                items={form.domain_blocklist}
                onAdd={(items) => updateField("domain_blocklist", items)}
                onRemove={(items) => updateField("domain_blocklist", items)}
                placeholder="domain"
                type="exclude"
              />
            </CardContent>
          </Card>

          <Card className="surface-secondary border-[#10B981]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#10B981]">App Allowlist</CardTitle>
              <CardDescription className="text-xs text-slate-500">Only bid on these app bundle IDs</CardDescription>
            </CardHeader>
            <CardContent>
              <BulkInputSection
                title=""
                items={form.app_allowlist}
                onAdd={(items) => updateField("app_allowlist", items)}
                onRemove={(items) => updateField("app_allowlist", items)}
                placeholder="bundle ID (e.g. com.example.app)"
                type="include"
              />
            </CardContent>
          </Card>

          <Card className="surface-secondary border-[#EF4444]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#EF4444]">App Blocklist</CardTitle>
              <CardDescription className="text-xs text-slate-500">Never bid on these apps</CardDescription>
            </CardHeader>
            <CardContent>
              <BulkInputSection
                title=""
                items={form.app_blocklist}
                onAdd={(items) => updateField("app_blocklist", items)}
                onRemove={(items) => updateField("app_blocklist", items)}
                placeholder="bundle ID"
                type="exclude"
              />
            </CardContent>
          </Card>

          {/* Ad Placements */}
          <Card className="surface-secondary border-[#F59E0B]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#F59E0B]">Ad Placements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Display Placements */}
              <div className="space-y-2">
                <Label className="text-slate-600">Display Placements (Include)</Label>
                <div className="flex flex-wrap gap-2">
                  {AD_PLACEMENTS_DISPLAY.map((p) => (
                    <Badge
                      key={p.value}
                      variant="secondary"
                      className={`cursor-pointer ${
                        form.ad_placements_display_include.includes(p.value)
                          ? "bg-[#10B981]/20 text-[#10B981]"
                          : "bg-slate-100 text-slate-500"
                      }`}
                      onClick={() => {
                        const list = form.ad_placements_display_include.includes(p.value)
                          ? form.ad_placements_display_include.filter(x => x !== p.value)
                          : [...form.ad_placements_display_include, p.value];
                        updateField("ad_placements_display_include", list);
                      }}
                    >
                      {p.label}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Native Placements */}
              <div className="space-y-2">
                <Label className="text-slate-600">Native Placements (Include)</Label>
                <div className="flex flex-wrap gap-2">
                  {AD_PLACEMENTS_NATIVE.map((p) => (
                    <Badge
                      key={p.value}
                      variant="secondary"
                      className={`cursor-pointer ${
                        form.ad_placements_native_include.includes(p.value)
                          ? "bg-[#10B981]/20 text-[#10B981]"
                          : "bg-slate-100 text-slate-500"
                      }`}
                      onClick={() => {
                        const list = form.ad_placements_native_include.includes(p.value)
                          ? form.ad_placements_native_include.filter(x => x !== p.value)
                          : [...form.ad_placements_native_include, p.value];
                        updateField("ad_placements_native_include", list);
                      }}
                    >
                      {p.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supply Tab */}
        <TabsContent value="supply" className="space-y-4 mt-4">
          <Card className="surface-secondary border-[#10B981]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#10B981]">Include SSPs/Exchanges</CardTitle>
              <CardDescription className="text-xs text-slate-500">Only bid through these supply sources</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {SUPPLY_SOURCES.map((ssp) => (
                  <Badge
                    key={ssp}
                    variant="secondary"
                    className={`cursor-pointer ${
                      form.supply_sources_include.includes(ssp)
                        ? "bg-[#10B981]/20 text-[#10B981]"
                        : "bg-slate-100 text-slate-500 hover:bg-[#10B981]/10"
                    }`}
                    onClick={() => {
                      const list = form.supply_sources_include.includes(ssp)
                        ? form.supply_sources_include.filter(s => s !== ssp)
                        : [...form.supply_sources_include, ssp];
                      updateField("supply_sources_include", list);
                    }}
                  >
                    {ssp}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="surface-secondary border-[#EF4444]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#EF4444]">Exclude SSPs/Exchanges</CardTitle>
              <CardDescription className="text-xs text-slate-500">Never bid through these supply sources</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {SUPPLY_SOURCES.map((ssp) => (
                  <Badge
                    key={ssp}
                    variant="secondary"
                    className={`cursor-pointer ${
                      form.supply_sources_exclude.includes(ssp)
                        ? "bg-[#EF4444]/20 text-[#EF4444]"
                        : "bg-slate-100 text-slate-500 hover:bg-[#EF4444]/10"
                    }`}
                    onClick={() => {
                      const list = form.supply_sources_exclude.includes(ssp)
                        ? form.supply_sources_exclude.filter(s => s !== ssp)
                        : [...form.supply_sources_exclude, ssp];
                      updateField("supply_sources_exclude", list);
                    }}
                  >
                    {ssp}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contextual Tab */}
        <TabsContent value="contextual" className="space-y-4 mt-4">
          <Card className="surface-secondary border-[#3B82F6]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#3B82F6]">Contextual Keywords</CardTitle>
            </CardHeader>
            <CardContent>
              <BulkInputSection
                title=""
                items={form.contextual_keywords}
                onAdd={(items) => updateField("contextual_keywords", items)}
                onRemove={(items) => updateField("contextual_keywords", items)}
                placeholder="keyword"
                type="include"
              />
              <div className="mt-4 space-y-2">
                <Label className="text-slate-600">Keyword Match Type</Label>
                <Select value={form.keyword_match_type} onValueChange={(v) => updateField("keyword_match_type", v)}>
                  <SelectTrigger className="surface-primary border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="surface-primary border-slate-200">
                    <SelectItem value="broad">Broad Match</SelectItem>
                    <SelectItem value="phrase">Phrase Match</SelectItem>
                    <SelectItem value="exact">Exact Match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technical Tab */}
        <TabsContent value="technical" className="space-y-4 mt-4">
          <Card className="surface-secondary border-[#10B981]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#10B981]">Include Browsers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {BROWSERS.map((browser) => (
                  <Badge
                    key={browser}
                    variant="secondary"
                    className={`cursor-pointer ${
                      form.browsers_include.includes(browser)
                        ? "bg-[#10B981]/20 text-[#10B981]"
                        : "bg-slate-100 text-slate-500 hover:bg-[#10B981]/10"
                    }`}
                    onClick={() => {
                      const list = form.browsers_include.includes(browser)
                        ? form.browsers_include.filter(b => b !== browser)
                        : [...form.browsers_include, browser];
                      updateField("browsers_include", list);
                    }}
                  >
                    {browser}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="surface-secondary border-[#EF4444]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#EF4444]">Exclude Browsers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {BROWSERS.map((browser) => (
                  <Badge
                    key={browser}
                    variant="secondary"
                    className={`cursor-pointer ${
                      form.browsers_exclude.includes(browser)
                        ? "bg-[#EF4444]/20 text-[#EF4444]"
                        : "bg-slate-100 text-slate-500 hover:bg-[#EF4444]/10"
                    }`}
                    onClick={() => {
                      const list = form.browsers_exclude.includes(browser)
                        ? form.browsers_exclude.filter(b => b !== browser)
                        : [...form.browsers_exclude, browser];
                      updateField("browsers_exclude", list);
                    }}
                  >
                    {browser}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
